import { isAccessToken, isRefreshToken, OktaAuth } from '@okta/okta-auth-js';

import { sendSentryError } from './utils/sendSentryError';

const issuer = process.env.OKTA_ISSUER || '';
const clientId = process.env.OKTA_CLIENT_ID || '';
const REFRESH_EARLY_SECONDS = 20 * 60; // 20 minutes in seconds

const oktaAuth: OktaAuth = new OktaAuth({
  issuer,
  clientId,
  redirectUri: `${window.location.origin}/login`,
  scopes: ['openid', 'profile', 'email', 'offline_access', 'idx_instancepermissions'],
  pkce: true,
  tokenManager: {
    storage: 'localStorage',
    storageKey: 'opal-app-okta-tokens',
    autoRenew: false, // Disable SDK auto-renewal to prevent iframe-based silent auth
  },
});

let inflightRenewal: Promise<string> | null = null;

// Stringify whatever was thrown so we never lose the original signal in
// Sentry. The Okta SDK occasionally throws plain objects (with errorCode/
// errorSummary) rather than Error instances; the previous code flattened
// those to the literal string "Unknown error".
const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const renewWithRefreshToken = async (): Promise<string> => {
  const tokenManager = oktaAuth.tokenManager;
  const refreshToken = await tokenManager.get('refreshToken');
  if (!isRefreshToken(refreshToken)) {
    throw new Error('No refresh token available for renewal');
  }

  const renewedTokens = await oktaAuth.token.renewTokensWithRefresh(
    { scopes: oktaAuth.options.scopes },
    refreshToken
  );

  if (!isAccessToken(renewedTokens.accessToken)) {
    throw new Error('No access token found during renewal');
  }

  tokenManager.setTokens(renewedTokens);
  return renewedTokens.accessToken.accessToken;
};

/**
 * Gets or renews the Okta access token if it's expiring within 20 minutes
 * @returns Promise<string> The access token (renewed if necessary)
 * @throws Error if unable to renew token or no token exists
 */
export const getOrRenewOptiIdToken = async (): Promise<string> => {
  try {
    const tokenManager = oktaAuth.tokenManager;
    const accessToken = await tokenManager.get('accessToken');

    if (!isAccessToken(accessToken)) {
      throw new Error('No access token found');
    }

    // Check if token expires within 20 minutes (1200 seconds)
    const currentTime = Math.floor(Date.now() / 1000);
    const expiresAt = accessToken.expiresAt || 0;
    const timeUntilExpiry = expiresAt - currentTime;

    if (timeUntilExpiry > REFRESH_EARLY_SECONDS) {
      // Token is still valid for more than threshold minutes
      return accessToken.accessToken;
    }

    // Renew using refresh token only — never fall back to iframe-based silent auth,
    // which hangs when the IdP sets X-Frame-Options: DENY.
    if (!inflightRenewal) {
      inflightRenewal = renewWithRefreshToken().finally(() => {
        inflightRenewal = null;
      });
    }
    return await inflightRenewal;
  } catch (error) {
    const isError = error instanceof Error;
    const originalMessage = extractErrorMessage(error);
    const errorMessage = `Failed to renew access token: ${originalMessage || 'no message'}`;

    // Forward Okta-specific error attributes so Sentry groups similar IdP
    // failures (invalid_grant, network errors, well-known fetch failures)
    // by their underlying type instead of bundling them all into one noisy
    // "renewal failed" issue.
    const oktaError = error as {
      errorCode?: unknown;
      errorSummary?: unknown;
      name?: unknown;
    };
    sendSentryError(isError ? error : new Error(errorMessage), {
      context: 'getOrRenewOptiIdToken',
      operation: 'token_renewal',
      errorName: oktaError.name,
      errorCode: oktaError.errorCode,
      errorSummary: oktaError.errorSummary,
    });

    // If the failure left storage without a refresh token, the session can
    // never recover on its own — Okta strips the rotated refresh token after
    // invalid_grant, and any subsequent renewal will throw "No refresh token
    // available for renewal" forever. Clear tokens and flip authState so
    // RequireAuth redirects to login. Without this, AccessDenied falls through
    // its non-401 branch (the renewal Error is not an AxiosError) and bounces
    // the user back to HOME with the same broken storage, looping.
    await clearSessionIfUnrecoverable();

    throw new Error(errorMessage);
  }
};

const clearSessionIfUnrecoverable = async (): Promise<void> => {
  try {
    const refreshToken = await oktaAuth.tokenManager.get('refreshToken').catch(() => null);
    if (isRefreshToken(refreshToken)) {
      return;
    }
    await oktaAuth.tokenManager.clear();
    await oktaAuth.authStateManager.updateAuthState();
  } catch (cleanupError) {
    sendSentryError(
      cleanupError instanceof Error ? cleanupError : new Error('Failed to clear stale auth state'),
      { context: 'getOrRenewOptiIdToken', operation: 'session_cleanup' }
    );
  }
};

export default oktaAuth;
